import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from 'lib/supabase';
import { useAuth } from 'contexts/AuthContext';
import { Download, Filter, Search, Mail, Star, CheckCircle, Clock, Trash2, MessageSquare } from 'lucide-react';

const AdminFeedbackDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [responseNotes, setResponseNotes] = useState('');

  // Check admin access
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (authLoading) return;
      
      if (!user) {
        navigate('/sign-in');
        return;
      }

      // Check if user is admin
      const adminEmails = ['admin@mybodytoday1.com', 'support@mybodytoday1.com'];
      if (!adminEmails?.includes(user?.email)) {
        navigate('/dashboard');
        return;
      }
    };

    checkAdminAccess();
  }, [user, authLoading, navigate]);

  // Fetch feedback submissions
  useEffect(() => {
    if (!user) return;
    fetchFeedback();
  }, [user, sortBy, sortOrder]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      let query = supabase?.from('feedback_submissions')?.select('*');

      // Apply sorting
      query = query?.order(sortBy, { ascending: sortOrder === 'asc' });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setFeedbackList(data || []);
      setError('');
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError(err?.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id, currentStatus) => {
    try {
      const { error: updateError } = await supabase?.from('feedback_submissions')?.update({ is_read: !currentStatus })?.eq('id', id);

      if (updateError) throw updateError;

      // Update local state
      setFeedbackList(prev => 
        prev?.map(item => 
          item?.id === id ? { ...item, is_read: !currentStatus } : item
        )
      );
    } catch (err) {
      console.error('Error updating read status:', err);
      alert('Failed to update status');
    }
  };

  const handleMarkAsResponded = async (id) => {
    try {
      const { error: updateError } = await supabase?.from('feedback_submissions')?.update({ 
          is_responded: true,
          response_notes: responseNotes
        })?.eq('id', id);

      if (updateError) throw updateError;

      // Update local state
      setFeedbackList(prev => 
        prev?.map(item => 
          item?.id === id ? { ...item, is_responded: true, response_notes: responseNotes } : item
        )
      );
      
      setSelectedFeedback(null);
      setResponseNotes('');
      alert('Marked as responded successfully');
    } catch (err) {
      console.error('Error marking as responded:', err);
      alert('Failed to update response status');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this feedback?')) return;

    try {
      const { error: deleteError } = await supabase?.from('feedback_submissions')?.delete()?.eq('id', id);

      if (deleteError) throw deleteError;

      setFeedbackList(prev => prev?.filter(item => item?.id !== id));
      alert('Feedback deleted successfully');
    } catch (err) {
      console.error('Error deleting feedback:', err);
      alert('Failed to delete feedback');
    }
  };

  // Filter feedback based on search and filters
  const filteredFeedback = feedbackList?.filter(item => {
    const matchesSearch = 
      item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.message?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesRating = filterRating === 'all' || item?.rating === parseInt(filterRating);
    
    const matchesStatus = 
      filterStatus === 'all' ||
      (filterStatus === 'unread' && !item?.is_read) ||
      (filterStatus === 'read' && item?.is_read) ||
      (filterStatus === 'responded' && item?.is_responded) ||
      (filterStatus === 'pending' && !item?.is_responded);

    return matchesSearch && matchesRating && matchesStatus;
  });

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Rating', 'Message', 'Read', 'Responded', 'Date'];
    const rows = filteredFeedback?.map(item => [
      item?.name,
      item?.email,
      item?.rating,
      item?.message,
      item?.is_read ? 'Yes' : 'No',
      item?.is_responded ? 'Yes' : 'No',
      new Date(item.created_at)?.toLocaleDateString()
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feedback-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
  };

  const stats = {
    total: feedbackList?.length,
    unread: feedbackList?.filter(f => !f?.is_read)?.length,
    pending: feedbackList?.filter(f => !f?.is_responded)?.length,
    avgRating: feedbackList?.length > 0 
      ? (feedbackList?.reduce((sum, f) => sum + f?.rating, 0) / feedbackList?.length)?.toFixed(1)
      : 0
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading feedback...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Feedback Dashboard</h1>
          <p className="text-gray-600">Manage and respond to user feedback submissions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Feedback</p>
                <p className="text-2xl font-bold text-gray-900">{stats?.total}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.unread}</p>
              </div>
              <Mail className="h-8 w-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Response</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Rating</p>
                <p className="text-2xl font-bold text-green-600">{stats?.avgRating}/5</p>
              </div>
              <Star className="h-8 w-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="inline h-4 w-4 mr-1" />
                Search
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                placeholder="Search by name, email, or message..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Filter className="inline h-4 w-4 mr-1" />
                Rating
              </label>
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e?.target?.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="pending">Pending Response</option>
                <option value="responded">Responded</option>
              </select>
            </div>
          </div>

          {/* Sort and Export */}
          <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="created_at">Date</option>
                <option value="rating">Rating</option>
                <option value="name">Name</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e?.target?.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <button
              onClick={exportToCSV}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Feedback Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredFeedback?.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No feedback submissions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rating
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFeedback?.map((feedback) => (
                    <tr key={feedback?.id} className={!feedback?.is_read ? 'bg-blue-50' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{feedback?.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{feedback?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {[...Array(5)]?.map((_, i) => (
                            <Star
                              key={i}
                              className={`h-4 w-4 ${
                                i < feedback?.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                          <span className="ml-2 text-sm text-gray-600">{feedback?.rating}/5</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-md truncate">
                          {feedback?.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {new Date(feedback.created_at)?.toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          {feedback?.is_responded ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Responded
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </span>
                          )}
                          {!feedback?.is_read && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Unread
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleMarkAsRead(feedback?.id, feedback?.is_read)}
                            className="text-blue-600 hover:text-blue-900"
                            title={feedback?.is_read ? 'Mark as unread' : 'Mark as read'}
                          >
                            <Mail className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedFeedback(feedback);
                              setResponseNotes(feedback?.response_notes || '');
                            }}
                            className="text-green-600 hover:text-green-900"
                            title="Respond"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(feedback?.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Response Modal */}
        {selectedFeedback && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Respond to Feedback</h3>
              
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2"><strong>From:</strong> {selectedFeedback?.name} ({selectedFeedback?.email})</p>
                <p className="text-sm text-gray-600 mb-2"><strong>Rating:</strong> {selectedFeedback?.rating}/5</p>
                <p className="text-sm text-gray-900"><strong>Message:</strong></p>
                <p className="text-sm text-gray-700 mt-1">{selectedFeedback?.message}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Notes (Internal)
                </label>
                <textarea
                  value={responseNotes}
                  onChange={(e) => setResponseNotes(e?.target?.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Add internal notes about how this was handled..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleMarkAsResponded(selectedFeedback?.id)}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Mark as Responded
                </button>
                <button
                  onClick={() => {
                    setSelectedFeedback(null);
                    setResponseNotes('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeedbackDashboard;